import { Animated, StyleSheet, Text, View } from 'react-native';
import { daysSince, formatWeight, isMaxStaleForProposal } from '@heraion/shared';
import type { MaxForProposal } from '@heraion/shared';

import { useAttentionPulse } from '../attentionCue';
import { formatRelativeAge } from '../formatRelativeAge';
import { IconButton } from '../primitives/IconButton';
import { useReducedMotion } from '../useReducedMotion';
import { weightUnitLabels } from '../weightUnitLabels';
import type { TFn } from '../i18nTypes';

import type { useTheme } from './theme';
import { useExerciseAgePulseEnabled } from './state/exerciseAgePulsePreference';

// PG-11 — the widget's own header row, and the only thing in it: the way to
// change the MAX for the exercise you are looking at.
//
// ⭐ Why a header and not a chip inside section 1 (Antenore, 2026-09-02:
// «nel widget dello storico, allineato a destra nell'header del widget»).
// Section 1 only exists when a set is open; on the exercise card there is no
// set, and the control would have vanished exactly where he also asked for it
// («anche il card dentro la card del catalogo»). One home, both surfaces.
//
// ⛔ No door, no button: without `onEditMax` the row does not render at all.
// The mount that cannot write a MAX must not show a control that looks like
// it can — the lesson `PE-26` paid for with two no-op adapters.
//
// THE RING. The clock next to this one (`HistoryIconButton`) pulses when the
// EXERCISE is old. This one pulses when the MAX is old, and they are different
// ages: his front squat was six days old and its MAX fourteen weeks. Putting
// both on the clock would have made one yellow mean two facts, which is his
// own rule about a control with a single affordance. So the signal sits on the
// control it is about, and tapping it is the thing that resolves it.
//
// ⛔ ONE level, not two. `exerciseStaleness` earns `warn` and `danger` from
// two thresholds MEASURED on 22 330 sets; the MAX has one threshold and it is
// a judgement call (`PROPOSAL_MAX_STALE_WEEKS`). A second tint here would be
// a number nobody derived, so red stays where it was measured.
//
// ⭐ AND THE VALUE IS WRITTEN, not only pulsed (Antenore, 2026-09-02, after the
// bench and the a11y audit landed on the same defect from two directions). A
// lone glyph over three text sections read as a decoration, and «this max is
// old» arrived as a yellow flicker indistinguishable from the clock's beside
// it — for a reduced-vision reader, two flickers and no words. So the row says
// «MAX 69 kg», and when it is stale it says how old: the tint stops being the
// only carrier, and the two rings stop being ambiguous because this one has a
// sentence next to it. The number is also one the logger showed NOWHERE else.

/** Gap between blinks — the same rare beat as the history clock, and for the
 *  same reason: he accepted the intermittent form BECAUSE it fires seldom. */
const PULSE_EVERY_MS = 15_000;

export function HistoryWidgetHeader({
  exerciseId,
  max,
  onEditMax,
  unit,
  locale,
  theme,
  t,
  nowMs,
}: {
  exerciseId: string;
  /** The athlete's current MAX for this exercise, when they have one. */
  max: MaxForProposal | undefined;
  /** Open the MAX editor for this exercise. ⛔ Absent = no control. */
  onEditMax?: () => void;
  /** The athlete's own unit — the row states the MAX in the number they read
   *  in, the same reason `useMaxEditor` stopped hardcoding kg (MX-6). */
  unit: 'kg' | 'lb';
  locale: string;
  theme: ReturnType<typeof useTheme>;
  t: TFn;
  /** Injectable clock, for the bench and the tests. */
  nowMs?: number;
}) {
  const { enabled } = useExerciseAgePulseEnabled();
  const reducedMotion = useReducedMotion();

  const now = nowMs ?? Date.now();
  // ⛔ "No MAX yet" is NOT a stale MAX. There is no age to point at, and the
  // button is still worth showing — it is how the athlete records their first
  // one. Same reading `HistoryIconButton` gives `never`.
  const stale = max !== undefined && isMaxStaleForProposal(max.setAt, now);

  const pulse = useAttentionPulse({
    active: stale,
    enabled,
    reducedMotion,
    cueKey: exerciseId,
    everyMs: PULSE_EVERY_MS,
  });

  if (!onEditMax) return null;

  const styles = makeStyles(theme);
  const unitLabels = weightUnitLabels(t);
  const ageDays =
    max?.setAt === undefined ? null : daysSince(Date.parse(max.setAt), now);

  // The age travels in the accessible name, exactly as LG-49 does it: the tint
  // carries the fact for the eye, the sentence carries it for everyone else.
  const accessibilityLabel =
    max === undefined
      ? t('mobile.logger.maxEditNoneA11y', {
          defaultValue: 'Set your max for this exercise',
        })
      : stale
        ? t('mobile.logger.maxEditStaleA11y', {
            defaultValue: 'Change your max, last set {{age}}',
            age: formatRelativeAge(t, ageDays),
          })
        : t('mobile.logger.maxEditA11y', {
            defaultValue: 'Change your max',
          });

  // The written half. ⛔ Whole keys per case, never a sentence glued from
  // fragments: the five locales put the age in different places.
  const line =
    max === undefined
      ? t('mobile.logger.maxHeaderNone', { defaultValue: 'No max yet' })
      : stale
        ? t('mobile.logger.maxHeaderStale', {
            defaultValue: 'MAX {{weight}} · {{age}}',
            weight: formatWeight(max.valueKg, unit, locale, unitLabels),
            age: formatRelativeAge(t, ageDays),
          })
        : t('mobile.logger.maxHeaderValue', {
            defaultValue: 'MAX {{weight}}',
            weight: formatWeight(max.valueKg, unit, locale, unitLabels),
          });

  const button = (
    <IconButton
      name="trophy"
      onPress={onEditMax}
      accessibilityLabel={accessibilityLabel}
      // ⛔ Uniform 8, NOT the {top,bottom} narrowing `HistoryIconButton`
      // uses. That one sits 4px from a neighbour and trims its horizontal
      // zone so the two do not overlap; this one is alone in its row, so
      // copying the trim would only have left a 36pt-wide target under the
      // 44 floor. The reason travels with the value, or the value gets
      // copied without it — which is what happened here.
      hitSlop={8}
      theme={theme}
    />
  );

  return (
    <View style={styles.header}>
      <Text style={stale ? styles.lineStale : styles.line} numberOfLines={1}>
        {line}
      </Text>
      {stale ? (
        <View style={styles.wrap}>
          {/* Tinted ring around the glyph, pulsing. ⛔ NOT the icon's own
              opacity: fading the control would take a tap target off screen
              between beats. Opacity-only → native driver. */}
          <Animated.View
            pointerEvents="none"
            style={[
              styles.cue,
              { opacity: pulse, borderColor: theme.palette.warn },
            ]}
          />
          {button}
        </View>
      ) : (
        button
      )}
    </View>
  );
}

const makeStyles = (theme: ReturnType<typeof useTheme>) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing.s2,
    },
    // ⛔ ink2, not ink3: this is the row's content, not a caption. The stale
    // form goes one step darker still — it is asking for a decision.
    line: {
      flexShrink: 1,
      color: theme.palette.ink2,
      fontSize: theme.typography.sizes.sm,
    },
    lineStale: {
      flexShrink: 1,
      color: theme.palette.ink,
      fontSize: theme.typography.sizes.sm,
      fontWeight: '600',
    },
    // The ring sits 2 outside the box (`cue`, right: -2) and the sheet has no
    // horizontal padding of its own, so at the screen edge its right side was
    // cut (DS-68). Margin on the wrapper brings the whole ring inside; ring and
    // clock are unchanged (Antenore, #216).
    wrap: { marginRight: 2 },
    cue: {
      position: 'absolute',
      top: -2,
      bottom: -2,
      left: -2,
      right: -2,
      borderWidth: 2,
      borderRadius: theme.radius.r2,
    },
  });
